/**
 * EVERY TEXT A CUSTOMER GETS SAYS WHO SENT IT, AND HOW TO STOP IT.
 *
 * Anthony, 2026-10-07: "fix the shop desk texts too."
 *
 * Each shop on the desk is registering its own A2P 10DLC brand and
 * campaign. A carrier reviewer reads the sample messages and asks two
 * things of every one: does it name the business that is sending it, and
 * does it say how to opt out. The packs in _ops/a2p found the desk failing
 * both on the texts a shop sends most -- the quote, the job page link, "on
 * my way", the four quote chases -- because those were written when
 * Supercanic was the only sender and "Hi Chelsea, on my way" needed no
 * signature.
 *
 * ─── ONE PLACE, NOT TWENTY TEMPLATES ───────────────────────────────────
 *
 * The templates live in a dozen files and more will be written. Asking each
 * of them to remember the brand and the opt-out is how the nudge ended up
 * the one message out of seven without its STOP line. So the two rules live
 * here, and the two send paths (server/tasks/guard.ts send() for automated
 * texts, /api/sms/send for the desk buttons) apply them to anything marked
 * as a customer template. A template that already names the shop or already
 * carries an opt-out is left alone, which is the guard against doubling:
 * applying this twice gives the same text as applying it once.
 *
 * What it never touches: a message a person typed. Free text from the
 * customer screen or the comms panel is a conversation, and a signature
 * stamped onto somebody's own words is a different message from the one
 * they wrote. Staff and owner alerts are not customer texts either.
 *
 * Pure, no imports, so the browser and the server say the same sentence.
 */

/** The opt-out line, in the words carriers look for. */
export const OPT_OUT = "Reply STOP to opt out.";

/**
 * Any opt-out instruction we have ever written, so an older template or a
 * hand-written "Reply STOP to unsubscribe" is not given a second one.
 */
const HAS_OPT_OUT = /\b(?:reply|txt|text|send|responda|envie|envíe)\s+stop\b|\bstop\s+to\s+(?:opt[\s-]?out|unsubscribe|stop|end)\b/i;

export const hasOptOut = (text: string): boolean => HAS_OPT_OUT.test(text);

/** The text with the opt-out line on the end, once. */
export function withOptOut(text: string): string {
  const t = String(text ?? "").trim();
  if (!t || hasOptOut(t)) return t;
  return `${t} ${OPT_OUT}`;
}

/**
 * THE NAME A CUSTOMER KNOWS THE SHOP BY.
 *
 * accounts.name is the legal-ish name somebody typed at signup, and
 * "Wrench Services LLC." is not what is painted on the van. The suffix is
 * dropped the same way the screens already drop it (src/ui/words.ts), so a
 * text and the page it links to call the shop the same thing. An empty
 * name stays empty: the caller decides what an unnamed shop is called.
 */
export function displayName(raw: string | null | undefined): string {
  return String(raw ?? "")
    .trim()
    .replace(/,?\s*\b(llc|l\.l\.c|inc|ltd|co|corp)\.?$/i, "")
    .replace(/[\s,]+$/, "")
    .trim();
}

/** Whether the text already says who is sending it. */
export function namesShop(text: string, shop: string): boolean {
  const s = shop.trim().toLowerCase();
  return Boolean(s) && String(text ?? "").toLowerCase().includes(s);
}

/**
 * THE WHOLE RULE: named, then opt-out, each only when missing.
 *
 * The name goes on the front as "Shop Name: ", the shape carriers and
 * customers both read as a sender, and the one that leaves the rest of the
 * sentence exactly as it was -- every pattern that recognises our own
 * messages in a thread ("did you get the quote", "still want us to come
 * out") still matches.
 */
export function customerText(shop: string | null | undefined, text: string): string {
  const name = displayName(shop);
  let t = String(text ?? "").trim();
  if (!t) return t;
  if (name && !namesShop(t, name)) t = `${name}: ${t}`;
  return withOptOut(t);
}

/**
 * WHERE A CUSTOMER LINK POINTS, given the shop's own domain.
 *
 * Mirrors server/desk/customer-site.ts baseFrom(), for the browser: a shop
 * whose accounts.domain is set sends links on that host, and a shop with
 * none keeps the host it has today. Every configured domain was checked on
 * 2026-10-07 to answer /healthz, /garage.html and /api/garage with the same
 * customer as shop.supercanic.com does.
 */
export function customerOrigin(domain: string | null | undefined, fallback: string): string {
  const host = String(domain ?? "").trim()
    .replace(/^https?:\/\//i, "").replace(/\/.*$/, "").replace(/\.$/, "");
  return host ? `https://${host}` : String(fallback ?? "").replace(/\/+$/, "");
}
